import { useTenant } from '../../hooks/useTenant';
import { ACTIONS } from '../tenancy/actions';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { MessageSquare, Send } from 'lucide-react';
import Avatar from '../../components/ui/Avatar';
import Spinner from '../../components/ui/Spinner';
import { getErrorMessage } from '../../lib/apiClient';
import { subscribeToThread } from '../../lib/realtime';
import { useAuthStore } from '../../store/authStore';
import { formatDate } from '../../utils/format';
import {
  setConversationDecision,
  chatKeys,
  insertChatMessage,
  listChatMessages,
  markThreadRead,
  postChatMessage,
  resolveChatThread,
} from './chatApi';

const POLL_INTERVAL_MS = 15_000;

/**
 * Live chat for a target (the old app's "Live chat" tab): oldest-first feed
 * with "Load older messages" history, auto-scroll on new messages,
 * Enter-to-send composer, and realtime delivery over the thread's socket room
 * — degrading to 15s polling whenever the socket isn't live.
 *
 * Defaults to a file target so the existing `fileId` callers keep working;
 * folder rows pass `targetType`/`targetId` instead.
 */
export default function FileChatPanel({ fileId, targetType = 'file', targetId = fileId }) {
  const queryClient = useQueryClient();
  const { allows } = useTenant();
  const meId = useAuthStore((s) => s.user?.id);
  const [live, setLive] = useState(false);
  const [draft, setDraft] = useState('');
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);

  // One thread per target, lazily created server-side.
  const { data: thread } = useQuery({
    queryKey: chatKeys.thread(targetType, targetId),
    queryFn: () => resolveChatThread(targetType, targetId),
    staleTime: 10000,
    refetchInterval: POLL_INTERVAL_MS,
  });
  const threadId = thread?.id;

  const messagesQuery = useInfiniteQuery({
    queryKey: chatKeys.messages(threadId),
    enabled: Boolean(threadId),
    initialPageParam: 1,
    queryFn: ({ pageParam }) => listChatMessages(threadId, { page: pageParam }),
    getNextPageParam: (last) => (last.meta.hasNextPage ? last.meta.page + 1 : undefined),
    // Polling is the fallback transport; the socket switches it off when live.
    refetchInterval: live ? false : POLL_INTERVAL_MS,
  });

  // Pages are newest-first; render oldest-first. The id-dedupe pass absorbs
  // page-boundary overlap after realtime prepends shift the windows.
  const messages = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const m of (messagesQuery.data?.pages ?? []).flatMap((p) => p.data)) {
      if (!seen.has(m.id)) {
        seen.add(m.id);
        out.push(m);
      }
    }
    return out.reverse();
  }, [messagesQuery.data]);
  const newestId = messages.at(-1)?.id;

  // Live subscription: join the thread room, insert pushes, fall back to
  // polling while not acknowledged.
  useEffect(() => {
    if (!threadId) return undefined;
    return subscribeToThread(threadId, {
      onLive: setLive,
      onMessage: (msg) => insertChatMessage(queryClient, threadId, msg),
    });
  }, [threadId, queryClient]);

  // Reading the panel advances my read cursor whenever the newest message changes.
  useEffect(() => {
    if (threadId && newestId) markThreadRead(threadId, newestId).catch(() => {});
  }, [threadId, newestId]);

  // Keep the view pinned to the bottom on new messages — but never while
  // older history is being prepended.
  useEffect(() => {
    if (!messagesQuery.isFetchingNextPage) {
      bottomRef.current?.scrollIntoView({ block: 'end' });
    }
  }, [newestId, messagesQuery.isFetchingNextPage]);

  const send = useMutation({
    mutationFn: (body) => postChatMessage(threadId, body),
    onSuccess: (msg) => insertChatMessage(queryClient, threadId, msg),
    onError: (err) => toast.error(getErrorMessage(err, 'Could not send message')),
  });

  const decision = useMutation({
    mutationFn: (value) => setConversationDecision(threadId, value),
    onSuccess: (updated) => {
      queryClient.setQueryData(chatKeys.thread(targetType, targetId), updated);
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const submit = () => {
    const body = draft.trim();
    if (!body || !threadId || send.isPending || thread?.isLocked) return;
    setDraft('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    send.mutate(body);
  };

  if (!threadId || messagesQuery.isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-b border-slate-200 p-3 text-sm">
        <p className="font-medium text-slate-700">
          {thread.decision === 'approved'
            ? 'Approved · conversation locked'
            : thread.decision === 'changes_requested'
              ? 'Changes requested'
              : 'Awaiting review'}
        </p>
        {allows(ACTIONS.CHAT_MODERATE) && (
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={decision.isPending || thread.isLocked}
              onClick={() => decision.mutate('approved')}
              className="rounded-md border border-green-600 px-2 py-1 text-green-700 disabled:opacity-50"
            >
              Mark Approved
            </button>
            <button
              type="button"
              disabled={decision.isPending}
              onClick={() => decision.mutate('changes_requested')}
              className="rounded-md border border-slate-300 px-2 py-1 text-slate-700"
            >
              Request Changes
            </button>
          </div>
        )}
      </div>
      {/* Feed */}
      <div className="scrollbar-thin min-h-0 flex-1 space-y-3 overflow-auto p-3">
        {messagesQuery.hasNextPage && (
          <button
            onClick={() => messagesQuery.fetchNextPage()}
            disabled={messagesQuery.isFetchingNextPage}
            className="mx-auto block rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-500 hover:bg-slate-200 disabled:opacity-50"
          >
            {messagesQuery.isFetchingNextPage ? 'Loading…' : 'Load older messages'}
          </button>
        )}

        {messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-slate-400">
            <MessageSquare className="h-8 w-8" />
            <p className="text-sm">No messages yet — start the conversation.</p>
          </div>
        ) : (
          messages.map((msg) => {
            const own = msg.senderId === meId;
            return (
              <div key={msg.id} className={clsx('flex gap-2', own && 'flex-row-reverse')}>
                {!own && (
                  <Avatar
                    name={msg.sender?.displayName}
                    className="h-7 w-7 bg-slate-200 text-[10px] font-semibold text-slate-600"
                  />
                )}
                <div className={clsx('min-w-0 max-w-[80%] space-y-0.5', own && 'text-right')}>
                  <p className="text-[11px] text-slate-400">
                    {!own && (
                      <span className="mr-1.5 font-medium text-slate-600">
                        {msg.sender?.displayName ?? 'Unknown'}
                      </span>
                    )}
                    {formatDate(msg.createdAt)}
                  </p>
                  <div
                    className={clsx(
                      'inline-block max-w-full whitespace-pre-wrap break-words rounded-2xl px-3 py-1.5 text-left text-sm',
                      own
                        ? 'rounded-br-md bg-brand-600 text-white'
                        : 'rounded-bl-md border border-slate-200 bg-slate-50 text-slate-700',
                    )}
                  >
                    {msg.body}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      <div className="flex items-end gap-2 border-t border-slate-200 p-3">
        <textarea
          ref={textareaRef}
          rows={1}
          value={draft}
          placeholder={
            thread.isLocked ? 'Approved conversations are read-only' : 'Write a message…'
          }
          disabled={thread.isLocked}
          onChange={(e) => {
            setDraft(e.target.value);
            e.target.style.height = 'auto';
            e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          className="min-h-9 flex-1 resize-none rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none"
        />
        <button
          onClick={submit}
          disabled={thread.isLocked || !draft.trim() || send.isPending}
          aria-label="Send message"
          className="rounded-lg bg-brand-600 p-2 text-white transition-colors hover:bg-brand-400 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
